import { For, Show, createMemo, createSignal, onCleanup, onMount, type JSX } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useApi, isOk } from '../api';
import type { KoffermaticPerfDirectiveDto, KoffermaticPerfReportDto } from '../api';
import Text from '../components/Text';
import Modal from '../components/Modal';
import { Badge } from '../components/Badge';
import { SearchInput } from '../components/SearchInput';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import { showConfirm } from '../components/Dialog';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { downloadFromApi } from '../services/downloadFile';

// The payload is whatever the client's schema version emitted, so it is read defensively rather than
// typed: a client one version ahead must still render.
interface PerfPayload {
    environment?: Record<string, unknown>;
    phases?: Array<Record<string, unknown>>;
    counters?: Array<Record<string, unknown>>;
    listeners?: Array<Record<string, unknown>>;
    sampling?: { samples?: number; byOwner?: Array<Record<string, unknown>>; byMethod?: Array<Record<string, unknown>> };
    stalls?: Array<{ elapsedMs?: number; phase?: string | null; listener?: string | null; stack?: string[] }>;
    worstFrames?: Array<{ periodMs?: number; renderMs?: number; worstListener?: string | null; topPhases?: Array<{ phase?: string; ms?: number }> }>;
    memory?: Record<string, unknown>;
    frames?: Record<string, unknown>;
}

const DEEP_MINUTES = 15;

function ms(value: number | null | undefined): string {
    if (value == null) return '-';
    return value >= 100 ? `${Math.round(value)} ms` : `${value.toFixed(1)} ms`;
}

function number(value: unknown): number {
    return typeof value === 'number' ? value : 0;
}

function text(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

function severity(report: KoffermaticPerfReportDto): 'error' | 'warning' | 'success' {
    const worst = report.frameMsMax ?? 0;
    if (worst >= 250) return 'error';
    if (worst >= 100) return 'warning';
    return 'success';
}

export default function AdminKoffermaticPerformance() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [reports, setReports] = createStore<KoffermaticPerfReportDto[]>([]);
    const [directives, setDirectives] = createSignal<KoffermaticPerfDirectiveDto[]>([]);
    const [total, setTotal] = createSignal(0);
    const [loading, setLoading] = createSignal(true);
    const [busy, setBusy] = createSignal<string | null>(null);

    const [player, setPlayer] = createSignal('');
    const [minWorstFrameMs, setMinWorstFrameMs] = createSignal(0);
    const [deepOnly, setDeepOnly] = createSignal(false);
    const [sinceHours, setSinceHours] = createSignal(48);

    const [openReport, setOpenReport] = createSignal<KoffermaticPerfReportDto | null>(null);
    const [openPayload, setOpenPayload] = createSignal<PerfPayload | null>(null);
    const [detailLoading, setDetailLoading] = createSignal(false);

    async function fetchReports() {
        try {
            const res = await api.koffermaticPerfAdmin.list({
                player: player() || undefined,
                minFrameMsMax: minWorstFrameMs() > 0 ? minWorstFrameMs() : undefined,
                deepOnly: deepOnly() ? true : undefined,
                sinceHours: sinceHours() > 0 ? sinceHours() : undefined,
                take: 100,
            });
            if (isOk(res.data)) {
                setReports(reconcile(res.data.value.reports ?? [], { key: 'id' }));
                setTotal(res.data.value.totalCount ?? 0);
            }
        } catch { /* the poll retries; a toast per tick would be noise */ }
        setLoading(false);
    }

    async function fetchDirectives() {
        try {
            const res = await api.koffermaticPerfAdmin.directives();
            if (isOk(res.data)) setDirectives(res.data.value.directives ?? []);
        } catch { /* the banner just stays empty */ }
    }

    async function openDetail(report: KoffermaticPerfReportDto) {
        setOpenReport(report);
        setOpenPayload(null);
        setDetailLoading(true);
        try {
            const res = await api.koffermaticPerfAdmin.detail(report.id!);
            if (isOk(res.data)) {
                setOpenPayload(JSON.parse(res.data.value.payloadJson ?? '{}') as PerfPayload);
            } else {
                toast?.error(t('KOFFERMATIC_PERF_DETAIL_FAILED'));
            }
        } catch {
            toast?.error(t('KOFFERMATIC_PERF_DETAIL_FAILED'));
        } finally {
            setDetailLoading(false);
        }
    }

    async function requestDeepProfile(report: KoffermaticPerfReportDto) {
        setBusy(report.id!);
        try {
            const res = await api.koffermaticPerfAdmin.requestDeepProfile(report.installId!, { minutes: DEEP_MINUTES });
            if (!isOk(res.data)) {
                toast?.error(t('KOFFERMATIC_PERF_DEEP_FAILED'));
                return;
            }
            toast?.success(t('KOFFERMATIC_PERF_DEEP_REQUESTED', report.playerName ?? report.installId ?? ''));
            await fetchDirectives();
        } catch {
            toast?.error(t('KOFFERMATIC_PERF_DEEP_FAILED'));
        } finally {
            setBusy(null);
        }
    }

    async function clearDeepProfile(directive: KoffermaticPerfDirectiveDto) {
        setBusy(directive.installId!);
        try {
            const res = await api.koffermaticPerfAdmin.clearDeepProfile(directive.installId!);
            if (!isOk(res.data)) {
                toast?.error(t('KOFFERMATIC_PERF_DEEP_CLEAR_FAILED'));
                return;
            }
            await fetchDirectives();
        } catch {
            toast?.error(t('KOFFERMATIC_PERF_DEEP_CLEAR_FAILED'));
        } finally {
            setBusy(null);
        }
    }

    async function deleteReport(report: KoffermaticPerfReportDto) {
        const confirmed = await showConfirm(
            t('KOFFERMATIC_PERF_DELETE_CONFIRM', report.playerName ?? report.installId ?? ''), true);
        if (!confirmed) return;
        setBusy(report.id!);
        try {
            const res = await api.koffermaticPerfAdmin.delete(report.id!);
            if (!isOk(res.data)) {
                toast?.error(t('KOFFERMATIC_PERF_DELETE_FAILED'));
                return;
            }
            await fetchReports();
        } catch {
            toast?.error(t('KOFFERMATIC_PERF_DELETE_FAILED'));
        } finally {
            setBusy(null);
        }
    }

    async function downloadReport(report: KoffermaticPerfReportDto) {
        const failed = await downloadFromApi(
            `/api/koffermatic/admin/performance/${report.id}/download`,
            `koffermatic-perf-${report.playerName ?? report.installId}.json`);
        if (failed) toast?.error(t('STATE_ERROR_GENERIC'));
    }

    onMount(() => {
        void fetchReports();
        void fetchDirectives();
        const refresh = window.setInterval(() => {
            if (document.visibilityState === 'visible' && !openReport()) {
                void fetchReports();
                void fetchDirectives();
            }
        }, 15000);
        onCleanup(() => clearInterval(refresh));
    });

    function applyFilters() {
        setLoading(true);
        void fetchReports();
    }

    const worstOfPage = createMemo(() => reports.reduce((worst, r) => Math.max(worst, r.frameMsMax ?? 0), 0));

    return (
        <div class="v" style={{ 'max-width': '1500px', margin: '0 auto', padding: 'var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                <h1 style={{ margin: 0 }}><Text val="KOFFERMATIC_PERF_TITLE" /></h1>
                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                    <Text val="KOFFERMATIC_PERF_SUBTITLE" />
                </span>
            </div>

            <div class="fcard h" style={{ 'align-items': 'flex-end', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                <label class="v" style={{ gap: 'var(--spacing-xs)', flex: '1', 'min-width': '16rem' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="KOFFERMATIC_PERF_FILTER_PLAYER" />
                    </span>
                    <SearchInput value={player()} onInput={setPlayer} placeholder={t('KOFFERMATIC_PERF_FILTER_PLAYER_PLACEHOLDER')} />
                </label>
                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="KOFFERMATIC_PERF_FILTER_WORST_FRAME" />
                    </span>
                    <select value={minWorstFrameMs()} onInput={(e) => setMinWorstFrameMs(parseInt(e.currentTarget.value, 10))}>
                        <option value="0">{t('KOFFERMATIC_PERF_FILTER_ANY')}</option>
                        <option value="50">50 ms</option>
                        <option value="100">100 ms</option>
                        <option value="250">250 ms</option>
                        <option value="500">500 ms</option>
                    </select>
                </label>
                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="KOFFERMATIC_PERF_FILTER_SINCE" />
                    </span>
                    <select value={sinceHours()} onInput={(e) => setSinceHours(parseInt(e.currentTarget.value, 10))}>
                        <option value="6">6h</option>
                        <option value="48">48h</option>
                        <option value="168">7d</option>
                        <option value="0">{t('KOFFERMATIC_PERF_FILTER_ANY')}</option>
                    </select>
                </label>
                <label class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-xs)' }}>
                    <input type="checkbox" checked={deepOnly()} onChange={(e) => setDeepOnly(e.currentTarget.checked)} />
                    <span style={{ 'font-size': 'var(--font-size-sm)' }}><Text val="KOFFERMATIC_PERF_FILTER_DEEP_ONLY" /></span>
                </label>
                <button class="btn btn-primary" onClick={applyFilters}>
                    <i class="fa-solid fa-filter" /> <Text val="KOFFERMATIC_PERF_APPLY" />
                </button>
            </div>

            <Show when={directives().length > 0}>
                <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                    <div style={{ 'font-weight': 'var(--font-weight-semibold)' }}>
                        <Text val="KOFFERMATIC_PERF_ACTIVE_DEEP" />
                    </div>
                    <For each={directives()}>{(directive) =>
                        <div class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-md)' }}>
                            <span style={{ 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-xs)' }}>
                                {directive.installId}
                            </span>
                            <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                                <Text val="KOFFERMATIC_PERF_DEEP_UNTIL" />: {formatDateTime(directive.deepProfileUntilUtc!)}
                                {directive.setByUsername ? ` · ${directive.setByUsername}` : ''}
                            </span>
                            <button class="btn btn-secondary btn-sm" disabled={busy() === directive.installId}
                                onClick={() => void clearDeepProfile(directive)}>
                                <Text val="KOFFERMATIC_PERF_DEEP_CLEAR" />
                            </button>
                        </div>
                    }</For>
                </div>
            </Show>

            <Show when={!loading()} fallback={
                <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                </div>
            }>
                <Show when={reports.length > 0} fallback={
                    <EmptyState icon="fa-gauge-high" title={t('KOFFERMATIC_PERF_EMPTY')}
                        hint={t('KOFFERMATIC_PERF_EMPTY_HINT')} />
                }>
                    <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'baseline' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            {t('KOFFERMATIC_PERF_COUNT', String(reports.length), String(total()))}
                        </span>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="KOFFERMATIC_PERF_WORST_ON_PAGE" />: {ms(worstOfPage())}
                        </span>
                    </div>

                    <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                        <For each={reports}>{(report) =>
                            <div class="fcard h" style={{ 'align-items': 'center', gap: 'var(--spacing-lg)', 'flex-wrap': 'wrap' }}>
                                <div class="v" style={{ gap: 'var(--spacing-xs)', 'min-width': '12rem', flex: '1' }}>
                                    <div class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-xs)' }}>
                                        <span style={{ 'font-weight': 'var(--font-weight-semibold)' }}>
                                            {report.playerName || t('KOFFERMATIC_PERF_UNKNOWN_PLAYER')}
                                        </span>
                                        <Show when={report.deep}>
                                            <Badge tone="info" icon="fa-magnifying-glass-chart">
                                                <Text val="KOFFERMATIC_PERF_DEEP_BADGE" />
                                            </Badge>
                                        </Show>
                                    </div>
                                    <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)' }}>
                                        {formatDateTime(report.windowStartUtc!)} · {report.modVersion} · {report.kind}
                                    </span>
                                </div>

                                <div class="v" style={{ gap: 'var(--spacing-xs)', 'min-width': '11rem' }}>
                                    <span style={{ 'font-size': 'var(--font-size-sm)' }}>
                                        {Math.round(report.fpsAverage ?? 0)} <Text val="KOFFERMATIC_PERF_FPS" />
                                        {' · p95 '}{ms(report.frameMsP95)}
                                    </span>
                                    <span class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-xs)', 'font-size': 'var(--font-size-xs)' }}>
                                        <Badge tone={severity(report)}>
                                            <Text val="KOFFERMATIC_PERF_WORST_FRAME" />: {ms(report.frameMsMax)}
                                        </Badge>
                                        <span style={{ color: 'var(--dark-text-muted)' }}>
                                            {report.framesOver100Ms} &gt;100 ms · {report.stallCount} <Text val="KOFFERMATIC_PERF_STALLS" />
                                        </span>
                                    </span>
                                </div>

                                <div class="v" style={{ gap: 'var(--spacing-xs)', flex: '1', 'min-width': '14rem' }}>
                                    <span style={{ 'font-size': 'var(--font-size-sm)' }}>
                                        <Text val="KOFFERMATIC_PERF_TOP_OFFENDER" />: {report.topOffender || '-'}
                                    </span>
                                    <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                                        {(report.topOffenderMsPerSecond ?? 0).toFixed(1)} <Text val="KOFFERMATIC_PERF_MS_PER_SECOND" />
                                        {' · '}{report.gcPauseMs} ms GC · {report.modCount} <Text val="KOFFERMATIC_PERF_MODS" />
                                    </span>
                                </div>

                                <div class="v" style={{ gap: 'var(--spacing-xs)', 'min-width': '12rem' }}>
                                    <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                                        {report.gpuRenderer || '-'}
                                    </span>
                                    <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                                        {report.osName} · {report.cpuCores} <Text val="KOFFERMATIC_PERF_CORES" /> · {report.maxHeapMb} MB
                                    </span>
                                </div>

                                <div class="h" style={{ gap: 'var(--spacing-xs)' }}>
                                    <button class="btn btn-primary btn-sm" onClick={() => void openDetail(report)}>
                                        <i class="fa-solid fa-list-check" /> <Text val="KOFFERMATIC_PERF_DETAILS" />
                                    </button>
                                    <button class="btn btn-secondary btn-sm" disabled={busy() === report.id}
                                        onClick={() => void requestDeepProfile(report)}>
                                        <i class="fa-solid fa-magnifying-glass-chart" /> <Text val="KOFFERMATIC_PERF_DEEP_REQUEST" />
                                    </button>
                                    <button class="btn btn-secondary btn-sm" onClick={() => void downloadReport(report)}>
                                        <i class="fa-solid fa-download" />
                                    </button>
                                    <button class="btn btn-danger btn-sm" disabled={busy() === report.id}
                                        onClick={() => void deleteReport(report)}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        }</For>
                    </div>
                </Show>
            </Show>

            <Modal open={openReport() !== null} onClose={() => setOpenReport(null)} maxWidth="1100px"
                busy={detailLoading()}
                title={<Text val="KOFFERMATIC_PERF_DETAIL_TITLE" />}
                subtitle={<span style={{ 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-xs)' }}>
                    {openReport()?.playerName} · {openReport()?.installId} · {openReport() ? formatDateTime(openReport()!.windowStartUtc!) : ''}
                </span>}>
                <Show when={openPayload()} fallback={
                    <div style={{ padding: 'var(--spacing-lg)', color: 'var(--dark-text-muted)' }}><Text val="LOADING" /></div>
                }>
                    {(payload) =>
                        <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                            <Section titleKey="KOFFERMATIC_PERF_SECTION_PHASES"
                                hintKey="KOFFERMATIC_PERF_SECTION_PHASES_HINT">
                                <Table rows={(payload().phases ?? []).map(phase => [
                                    text(phase.phase),
                                    String(number(phase.calls)),
                                    ms(number(phase.totalMs)),
                                    ms(number(phase.maxMs)),
                                    number(phase.msPerSecond).toFixed(1),
                                ])} headers={['', t('KOFFERMATIC_PERF_COL_CALLS'), t('KOFFERMATIC_PERF_COL_TOTAL'),
                                    t('KOFFERMATIC_PERF_COL_MAX'), t('KOFFERMATIC_PERF_MS_PER_SECOND')]} />
                            </Section>

                            <Section titleKey="KOFFERMATIC_PERF_SECTION_LISTENERS"
                                hintKey="KOFFERMATIC_PERF_SECTION_LISTENERS_HINT">
                                <Table rows={(payload().listeners ?? []).map(listener => [
                                    text(listener.mod),
                                    `${text(listener.handler)} (${text(listener.event)})`,
                                    String(number(listener.invocations)),
                                    ms(number(listener.totalMs)),
                                    ms(number(listener.maxMs)),
                                    number(listener.msPerSecond).toFixed(1),
                                ])} headers={[t('KOFFERMATIC_PERF_COL_MOD'), t('KOFFERMATIC_PERF_COL_HANDLER'),
                                    t('KOFFERMATIC_PERF_COL_CALLS'), t('KOFFERMATIC_PERF_COL_TOTAL'),
                                    t('KOFFERMATIC_PERF_COL_MAX'), t('KOFFERMATIC_PERF_MS_PER_SECOND')]} />
                            </Section>

                            <Section titleKey="KOFFERMATIC_PERF_SECTION_SAMPLING"
                                hintKey="KOFFERMATIC_PERF_SECTION_SAMPLING_HINT">
                                <Table rows={(payload().sampling?.byOwner ?? []).map(owner => [
                                    text(owner.name),
                                    String(number(owner.samples)),
                                    `${number(owner.percent).toFixed(1)} %`,
                                ])} headers={[t('KOFFERMATIC_PERF_COL_OWNER'), t('KOFFERMATIC_PERF_COL_SAMPLES'), '%']} />
                                <Table rows={(payload().sampling?.byMethod ?? []).map(method => [
                                    text(method.name),
                                    String(number(method.samples)),
                                    `${number(method.percent).toFixed(1)} %`,
                                ])} headers={[t('KOFFERMATIC_PERF_COL_METHOD'), t('KOFFERMATIC_PERF_COL_SAMPLES'), '%']} />
                            </Section>

                            <Section titleKey="KOFFERMATIC_PERF_SECTION_WORST_FRAMES"
                                hintKey="KOFFERMATIC_PERF_SECTION_WORST_FRAMES_HINT">
                                <Table rows={(payload().worstFrames ?? []).map(frame => [
                                    ms(frame.periodMs),
                                    ms(frame.renderMs),
                                    (frame.topPhases ?? []).map(phase => `${phase.phase} ${ms(phase.ms)}`).join(', ') || '-',
                                    frame.worstListener ?? '-',
                                ])} headers={[t('KOFFERMATIC_PERF_COL_PERIOD'), t('KOFFERMATIC_PERF_COL_RENDER'),
                                    t('KOFFERMATIC_PERF_COL_PHASES'), t('KOFFERMATIC_PERF_COL_LISTENER')]} />
                            </Section>

                            <Section titleKey="KOFFERMATIC_PERF_SECTION_STALLS"
                                hintKey="KOFFERMATIC_PERF_SECTION_STALLS_HINT">
                                <For each={payload().stalls ?? []}>{(stall) =>
                                    <div class="v" style={{ gap: 'var(--spacing-xs)', 'margin-bottom': 'var(--spacing-md)' }}>
                                        <div style={{ 'font-size': 'var(--font-size-sm)' }}>
                                            {ms(stall.elapsedMs)}
                                            {stall.phase ? ` · ${stall.phase}` : ''}
                                            {stall.listener ? ` · ${stall.listener}` : ''}
                                        </div>
                                        <pre style={{
                                            margin: 0, padding: 'var(--spacing-sm)', overflow: 'auto',
                                            'max-height': '12rem', 'font-size': 'var(--font-size-xs)',
                                            background: 'var(--dark-bg-elevated)', 'border-radius': 'var(--radius-sm)',
                                        }}>{(stall.stack ?? []).join('\n')}</pre>
                                    </div>
                                }</For>
                                <Show when={(payload().stalls ?? []).length === 0}>
                                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                                        <Text val="KOFFERMATIC_PERF_NO_STALLS" />
                                    </span>
                                </Show>
                            </Section>

                            <Section titleKey="KOFFERMATIC_PERF_SECTION_ENVIRONMENT">
                                <Table rows={Object.entries(payload().environment ?? {})
                                    .filter(([, value]) => typeof value !== 'object')
                                    .map(([key, value]) => [key, String(value)])}
                                    headers={['', '']} />
                            </Section>
                        </div>
                    }
                </Show>
            </Modal>
        </div>
    );
}

function Section(props: { titleKey: string; hintKey?: string; children: JSX.Element }) {
    return (
        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
            <h3 style={{ margin: 0, 'font-size': 'var(--font-size-md)' }}><Text val={props.titleKey} /></h3>
            <Show when={props.hintKey}>
                <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                    <Text val={props.hintKey!} />
                </span>
            </Show>
            {props.children}
        </div>
    );
}

// Wide rows scroll inside the section rather than pushing the modal sideways.
function Table(props: { headers: string[]; rows: string[][] }) {
    return (
        <Show when={props.rows.length > 0} fallback={
            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                <Text val="KOFFERMATIC_PERF_NO_ROWS" />
            </span>
        }>
            <div style={{ overflow: 'auto', 'max-height': '18rem' }}>
                <table style={{ 'border-collapse': 'collapse', width: '100%', 'font-size': 'var(--font-size-xs)' }}>
                    <thead>
                        <tr>
                            <For each={props.headers}>{(header) =>
                                <th style={{ 'text-align': 'left', padding: 'var(--spacing-xs)', color: 'var(--dark-text-muted)', 'font-weight': 'var(--font-weight-normal)' }}>
                                    {header}
                                </th>
                            }</For>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={props.rows}>{(row) =>
                            <tr>
                                <For each={row}>{(cell, index) =>
                                    <td style={{
                                        padding: 'var(--spacing-xs)',
                                        'font-family': index() === 0 ? 'var(--font-family-mono)' : undefined,
                                        'white-space': 'nowrap',
                                    }}>{cell}</td>
                                }</For>
                            </tr>
                        }</For>
                    </tbody>
                </table>
            </div>
        </Show>
    );
}
